import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { ImageProps, getTokenValue, Token, styled } from 'tamagui'

import { Image } from '../Image'

import { UserPrimaryPhotoNew_user$key } from './__generated__/UserPrimaryPhotoNew_user.graphql'

const UserPrimaryPhotoFrame = styled(Image, {
  name: 'UserPrimaryPhoto',
  objectFit: 'cover',
  // @ts-expect-error objectPosition is valid CSS
  objectPosition: 'center',
  overflow: 'hidden',
  aspectRatio: 1,
  flexShrink: 0,
  boxShadow: '0 0 0 2px var(--background)',
  // boxShadow: '0 0 0 16px #FF0000',
  borderColor: '$borderColor',
  borderWidth: 1,
  backgroundColor: '$background',

  variants: {
    thinShadow: {
      true: {
        boxShadow: '0 0 0 1px var(--background)',
      },
    },
  },

  defaultVariants: {
    thinShadow: false,
  },
} as const)

const GravatarWithFallback = React.forwardRef<
  HTMLImageElement,
  {
    emailMd5: string
    defaultAvatarUrl: string | null
    alt: string
    parsedSize: number
    size: Token
    [key: string]: unknown
  }
>(({ emailMd5, defaultAvatarUrl, alt, parsedSize, size, ...props }, ref) => {
  // Single element: gravatar as <img> src, default avatar as CSS background-image.
  // When gravatar is transparent (d=blank, no gravatar exists), the CSS background shows through.
  // If the gravatar request fails entirely, onError swaps to the default avatar.
  return (
    <UserPrimaryPhotoFrame
      {...props}
      alt={alt}
      ref={ref}
      src={`https://gravatar.com/avatar/${emailMd5}?d=blank&s=${parsedSize * 3}`}
      loading="lazy"
      width={parsedSize}
      height={parsedSize}
      borderRadius={size}
      style={{
        backgroundImage: defaultAvatarUrl ? `url(${defaultAvatarUrl})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
        if (defaultAvatarUrl) {
          (e.target as HTMLImageElement).src = defaultAvatarUrl
        }
      }}
    />
  )
})

export const UserPrimaryPhoto = UserPrimaryPhotoFrame.styleable<{
  user: UserPrimaryPhotoNew_user$key
  size: Token
}>(({ user, size = '$5', ...props }, ref) => {
  const data = useFragment(
    graphql`
      fragment UserPrimaryPhotoNew_user on User
      @argumentDefinitions(
        imageSize: { type: "Int", defaultValue: 100 }
        dpr: { type: "Int", defaultValue: 3 }
      ) {
        firstName
        lastName
        rowId
        emailMd5

        primaryPhoto {
          transformUrl(
            transform: {
              w: $imageSize
              h: $imageSize
              dpr: $dpr
              fo: "face"
              fit: "cover"
            }
          )
        }

        # Signed URL for default avatar when no custom photo is set
        defaultAvatarUrl(
          transform: { w: $imageSize, h: $imageSize, dpr: $dpr }
        )
      }
    `,
    user
  )

  const parsedSize = useMemo(() => {
    return getTokenValue(size, 'size') as number
  }, [size])

  if (data.primaryPhoto?.transformUrl) {
    return (
      <UserPrimaryPhotoFrame
        {...props}
        alt={`Primary Photo for ${data.firstName} ${data.lastName}`}
        ref={ref}
        src={data.primaryPhoto.transformUrl}
        width={parsedSize}
        height={parsedSize}
        borderRadius={size}
      />
    )
  }

  const alt = `Primary Photo for ${data.firstName} ${data.lastName}`

  // No primary photo: show default avatar with optional Gravatar overlay.
  // Default avatar renders as CSS background-image; gravatar (d=blank) as <img> src.
  // If the user has a real Gravatar it covers the default; otherwise the transparent
  // blank image lets the CSS background show through.
  if (data.emailMd5) {
    return (
      <GravatarWithFallback
        emailMd5={data.emailMd5}
        defaultAvatarUrl={data.defaultAvatarUrl}
        alt={alt}
        parsedSize={parsedSize}
        size={size}
        ref={ref}
        {...props}
      />
    )
  }

  return (
    <UserPrimaryPhotoFrame
      {...(props as any)}
      alt={alt}
      ref={ref}
      src={data.defaultAvatarUrl}
      width={parsedSize}
      height={parsedSize}
      borderRadius={size}
    />
  )
})

export const UserDefaultPhoto = UserPrimaryPhotoFrame.styleable<
  { defaultAvatarUrl: string } & Omit<ImageProps, 'source'>
>(({ defaultAvatarUrl, ...props }, ref) => {
  return <UserPrimaryPhotoFrame {...(props as any)} ref={ref} src={defaultAvatarUrl} />
})
